/**
 * The dashboard's inference vocabulary — one spelling of each mode name, each Inference_Failure kind,
 * and each remediation sentence (Feature: customer-managed-inference, tasks 17.2, 17.3, 18.1, 18.2;
 * Requirements 7.4, 7.7, 10.5, 13.8; Design: §Components 22).
 *
 * ## Why these live here rather than on the page that first needed them
 *
 * Three surfaces name an Inference_Mode or an Inference_Failure, and they are owned by different
 * requirements:
 *
 *  - {@link import('../pages/InferenceConfigurationPage').InferenceConfigurationPage} — an
 *    administrator repairing their own account (Requirements 7.4, 7.7, 13.4).
 *  - {@link import('../pages/CheckGeneratePage').CheckGeneratePage} — an editor being told which
 *    account will process the document they are about to submit, and what went wrong when the model
 *    could not be reached (Requirements 7.4, 13.8).
 *  - {@link import('../pages/CheckDraftReviewPage').CheckDraftReviewPage} — a reviewer seeing where a
 *    draft was produced before accepting it (Requirement 11.6).
 *
 * A second spelling of "add the missing `bedrock:InvokeModel` resource" would be a second thing to
 * keep true. Worse, the two spellings would drift in the direction that matters most: the remediation
 * is an instruction about a **customer's own AWS account**, and two versions of it are two versions of
 * what the customer is being asked to change. So the tables are extracted rather than copied, and the
 * pages differ only in how they present them.
 *
 * ## What is deliberately *not* here
 *
 * `VERIFICATION_LABELS` stays on the configuration page: verification state is admin-only
 * (Requirements 14.2, 14.3), so no other surface can read one, and moving it here would suggest
 * otherwise. Nothing in this module renders anything either — a shared *component* would have forced
 * the generation surfaces to carry the configuration page's verify action, which an editor cannot
 * invoke.
 *
 * ## The word "below"
 *
 * Four remediation strings say "below", because they name a document or control that is on the
 * configuration page. On a generation surface there is no "below", so those surfaces render the
 * sentence with an explicit pointer at `/inference` beside it rather than editing the sentence — see
 * `CheckGeneratePage`'s `InferenceFailureNotice`. Rewording per surface is what this module exists to
 * avoid.
 */

import type { InferenceFailureKind, InferenceMode } from '../api/types';

/** How the dashboard names each {@link InferenceMode}. */
export const MODE_LABELS: Record<InferenceMode, string> = {
  soroush_hosted: 'Soroush-hosted (the default)',
  customer_hosted: 'Customer-hosted (your own AWS account)',
};

/** How the dashboard names each {@link InferenceFailureKind} — the *what*, not the *what to do*. */
export const FAILURE_LABELS: Record<InferenceFailureKind, string> = {
  not_verified: 'This account is customer-hosted but its configuration is not verified',
  role_assumption_refused: 'Soroush could not assume your Customer_Inference_Role',
  external_id_rejected: 'The role exists and the External ID was not accepted',
  region_unusable: 'The region does not serve what this configuration names',
  profile_unavailable: 'The inference profile does not exist, or is not ACTIVE in that region',
  access_denied: 'Your permission policy does not admit the invocation',
  capability_unsupported: 'The model did not honour a forced tool call',
  probe_throttled: 'The capability probe was throttled, so the configuration is not yet judged',
  probe_failed: 'The capability probe did not complete',
  refused_model_resource: 'The configuration names a model resource Soroush will not invoke',
  geography_unacknowledged: 'A cross-geography profile has no acknowledgement for that identifier',
};

/**
 * What the customer changes **in their own account** to clear each failure (Requirement 7.4).
 *
 * Written as instructions to the account's owner rather than as diagnostics, because that is the only
 * place any of these can be fixed: the trust policy, the permission policy, the Bedrock quota, and the
 * region's model availability are all theirs.
 */
export const FAILURE_REMEDIATION: Record<InferenceFailureKind, string> = {
  not_verified:
    'Request verification below. Until the configuration verifies, customer-hosted generation is ' +
    'refused rather than falling back to Soroush’s account.',
  role_assumption_refused:
    'Repair the trust policy on the role: it must allow sts:AssumeRole for the Inference Principal ' +
    'named above. The exact document is in “Trust policy” below.',
  external_id_rejected:
    'Update the sts:ExternalId condition on the role’s trust policy to the External ID shown on this ' +
    'page. Until it names the current value, role assumption is refused.',
  region_unusable:
    'Choose a region that serves the inference profiles this configuration names, or choose profiles ' +
    'the region serves. Availability is per region and per account.',
  profile_unavailable:
    'Enable the model in your own account, or name a profile that is ACTIVE in that region. Model ' +
    'access is granted per account in the Bedrock console.',
  access_denied:
    'Add the missing bedrock:InvokeModel resource to the role’s permission policy. Copy the whole ' +
    '“Permission policy” below: a cross-region profile authorizes against the profile ARN *and* the ' +
    'underlying foundation-model ARN, so a policy naming only the profile deploys cleanly and then ' +
    'fails the first real invocation.',
  capability_unsupported:
    'Choose a model that honours a forced tool call. Soroush constrains generated output to a schema ' +
    'by forcing a tool call, so a model that ignores one cannot be used.',
  probe_throttled:
    'Raise your own Bedrock quota for this model, or request verification again shortly. Nothing is ' +
    'wrong with the configuration as far as Soroush can tell — the probe was throttled before it ' +
    'could reach a verdict.',
  probe_failed:
    'Request verification again. If it keeps failing, check that the role and the profile named above ' +
    'are the ones you meant and that the model is enabled in your account.',
  refused_model_resource:
    'Name an Amazon Bedrock foundation model or inference profile. A fine-tuned, distilled, ' +
    'custom-imported, provisioned-throughput, or non-Bedrock resource cannot be used, because Soroush ' +
    'cannot evidence what an adapted model was adapted on.',
  geography_unacknowledged:
    'Acknowledge the cross-geography profile below, or name a single-geography profile instead. The ' +
    'acknowledgement covers the exact identifier it was given for.',
};

/**
 * The one failure that is **not** a fault (Requirement 10.5).
 *
 * A throttled probe means the platform could not reach a verdict, which is "we have not been able to
 * tell yet" and not "your configuration is wrong". It is the distinction every failure region turns
 * on: the throttled arm is a `role="status"`, everything else is a `role="alert"` naming what to fix.
 */
export function isUnjudgedFailure(kind: InferenceFailureKind): boolean {
  return kind === 'probe_throttled';
}
